import React, { useEffect, Fragment } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, ArrowRightIcon, ArrowDownIcon, SearchIcon, SparklesIcon, ShieldCheckIcon, CheckCircle2Icon, CornerDownLeftIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../components/LanguageContext';
import { trackEvent as track } from '../utils/analytics';
const reveal = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-80px'
  },
  transition: {
    duration: 0.7
  }
};
const COMPARISON_ROWS = [{
  traditional: 'Export, filter and cross-reference manually',
  traditionalFr: 'Exporter, filtrer et recouper manuellement',
  bciti: 'Ask in plain language',
  bcitiFr: 'Demander en langage clair'
}, {
  traditional: 'One report per module',
  traditionalFr: 'Un rapport par module',
  bciti: 'All data, one conversation',
  bcitiFr: 'Toutes les données, une seule conversation'
}, {
  traditional: 'Ask IT for a data extract',
  traditionalFr: 'Demander un extrait de données aux TI',
  bciti: 'Immediate answer',
  bcitiFr: 'Réponse immédiate'
}, {
  traditional: 'Can I trust this number?',
  traditionalFr: 'Puis-je me fier à ce chiffre\u00A0?',
  bciti: 'Verified and auditable answer',
  bcitiFr: 'Réponse vérifiée et traçable'
}];
const WORKFLOW = [{
  en: 'Per-Module Analytics',
  fr: 'Analytique par module'
}, {
  en: 'ATNA Unifies',
  fr: 'ATNA unifie'
}, {
  en: 'Plain-Language Question',
  fr: 'Question en langage clair'
}, {
  en: 'Verified Answer',
  fr: 'Réponse vérifiée'
}, {
  en: 'Decision',
  fr: 'Décision'
}];
const MODULE_LIST = [{
  en: 'Identity',
  fr: 'Identité'
}, {
  en: 'Communications',
  fr: 'Communications'
}, {
  en: 'Collect',
  fr: 'Collecte'
}, {
  en: 'Requests',
  fr: 'Requêtes'
}, {
  en: 'Analytics',
  fr: 'Analytique'
}];
const EXAMPLE_QUESTIONS = [{
  en: 'How many digital cards are actively used each month?',
  fr: 'Combien de cartes numériques sont activement utilisées chaque mois\u00A0?'
}, {
  en: 'How many people subscribe to our news?',
  fr: 'Combien de personnes sont abonnées à nos nouvelles\u00A0?'
}, {
  en: "What's the share of requests by topic?",
  fr: 'Quelle est la répartition des requêtes par sujet\u00A0?'
}, {
  en: 'Which areas have the lowest engagement this quarter?',
  fr: 'Quels secteurs ont le plus faible engagement ce trimestre\u00A0?'
}];
const SUPPORTING_CAPS = [{
  en: 'Per-Module Analytics',
  fr: 'Analytique par module'
}, {
  en: 'Segmentation',
  fr: 'Segmentation'
}, {
  en: 'Period Comparison',
  fr: 'Comparaison de périodes'
}, {
  en: 'Reporting',
  fr: 'Production de rapports'
}, {
  en: 'Council Reports',
  fr: 'Rapports au conseil'
}, {
  en: 'Exportable Reports',
  fr: 'Rapports exportables'
}];
const AREA_PERFORMANCE = [{
  area: 'Area A',
  areaFr: 'Secteur A',
  value: 84
}, {
  area: 'Area B',
  areaFr: 'Secteur B',
  value: 71
}, {
  area: 'Area C',
  areaFr: 'Secteur C',
  value: 38
}, {
  area: 'Area D',
  areaFr: 'Secteur D',
  value: 66
}, {
  area: 'Area E',
  areaFr: 'Secteur E',
  value: 79
}];
const TREND = [42, 48, 51, 55, 58, 63, 67, 72];
function PreviewBadge({
  lang
}: {
  lang: string;
}) {
  return <span className="inline-flex items-center gap-2 font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#4F46E5] border border-[#4F46E5]/30 px-2.5 py-1" data-id="element-807">
      <span className="inline-block w-1.5 h-1.5 bg-[#4F46E5]" aria-hidden="true" data-id="element-808" />
      {lang === 'FR' ? 'Aperçu produit' : 'Product Preview'}
    </span>;
}
export function Analytics() {
  const {
    lang
  } = useLanguage();
  const pct = (n: number | string) => lang === 'FR' ? `${n}\u00A0%` : `${n}%`;
  useEffect(() => {
    track('Page Viewed', {
      page: 'Analytics'
    });
  }, []);
  return <div className="w-full min-h-screen bg-white" data-id="element-809">
      <Header data-id="element-810" />

      <main className="pt-[120px]" data-id="element-811">
        {/* SECTION 1: HOOK — conversational intelligence */}
        <section className="pt-24 pb-32 px-6" data-id="element-812">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-813">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8
          }} data-id="element-814">
              <div className="font-sans text-xs font-semibold tracking-[0.2em] text-[#4F46E5] uppercase mb-6" data-id="element-815">
                {lang === 'FR' ? 'BCITI Analytique' : 'BCITI Analytics'}
              </div>
              <h1 className="font-serif text-5xl md:text-6xl text-[#121212] leading-tight mb-8" data-id="element-816">
                {lang === 'FR' ? 'Posez la question. Obtenez la réponse. Vérifiée.' : 'Ask the question. Get the answer. Verified.'}
              </h1>
              <p className="font-sans text-xl text-[#121212]/70 leading-relaxed mb-4" data-id="element-817">
                {lang === 'FR' ? 'Chaque module possède déjà son analytique. ATNA les réunit.' : 'Every module already has analytics. ATNA brings them together.'}
              </p>
              <p className="font-sans text-lg text-[#121212]/60 leading-relaxed mb-10" data-id="element-818">
                {lang === 'FR' ? 'Votre organisation peut interroger ses données directement, en langage clair.' : 'Your organization can query its data directly, in plain language.'}
              </p>

              <div className="flex flex-wrap items-center gap-4 mb-10" data-id="element-819">
                <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
                location: 'Analytics Hero'
              })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-820">
                  {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                  <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-821" />
                </Link>
                <a href="#proof" onClick={e => {
                e.preventDefault();
                document.getElementById('proof')?.scrollIntoView({
                  behavior: 'smooth'
                });
                track('Proof CTA Clicked', {
                  location: 'Analytics Hero'
                });
              }} className="inline-flex items-center gap-2 px-8 py-4 border border-[#E4E6EC] text-[#121212] text-base font-medium rounded-none hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-822">
                  {lang === 'FR' ? 'Voir en production' : 'See It In Production'}
                  <ArrowRightIcon className="w-4 h-4" data-id="element-823" />
                </a>
              </div>

              <p className="font-sans text-sm text-[#121212]/45" data-id="element-824">
                {lang === 'FR' ? 'Analytique en production à Clarence-Rockland' : 'Production analytics live in Clarence-Rockland'}
              </p>
            </motion.div>

            {/* HERO VISUAL: Conversational intelligence terminal */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="flex justify-center" data-id="element-825">
              <div className="w-full max-w-md border border-[#E4E6EC] bg-[#FAFAFB] p-4" data-id="element-826">
                <div className="border border-[#E4E6EC] bg-white" data-id="element-827">
                  {/* Header */}
                  <div className="flex items-center justify-between px-5 py-4 border-b border-[#E4E6EC]" data-id="element-828">
                    <div className="flex items-center gap-2" data-id="element-829">
                      <span className="inline-block w-3 h-3 bg-[#4F46E5]" aria-hidden="true" data-id="element-830" />
                      <span className="font-sans text-xs font-semibold tracking-[0.16em] uppercase text-[#121212]/70" data-id="element-831">
                        {lang === 'FR' ? 'Intelligence conversationnelle' : 'Conversational Intelligence'}
                      </span>
                    </div>
                    <PreviewBadge lang={lang} data-id="element-832" />
                  </div>

                  {/* Query field */}
                  <div className="px-5 py-5 border-b border-[#E4E6EC]" data-id="element-833">
                    <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-3" data-id="element-834">
                      {lang === 'FR' ? 'Interrogez vos données' : 'Ask your data'}
                    </div>
                    <div className="flex items-center gap-3 border border-[#121212]/15 px-4 py-3.5" data-id="element-835">
                      <SearchIcon className="w-4 h-4 text-[#4F46E5] shrink-0" data-id="element-836" />
                      <span className="font-sans text-sm text-[#121212] flex-1" data-id="element-837">
                        {lang === 'FR' ? 'Quels secteurs ont le plus faible engagement ce trimestre\u00A0?' : 'Which areas have the lowest engagement this quarter?'}
                      </span>
                      <CornerDownLeftIcon className="w-4 h-4 text-[#121212]/30 shrink-0" data-id="element-838" />
                    </div>
                  </div>

                  {/* Answer */}
                  <div className="px-5 py-5 border-b border-[#E4E6EC]" data-id="element-839">
                    <div className="flex items-center gap-2 mb-3" data-id="element-840">
                      <SparklesIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-841" />
                      <span className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40" data-id="element-842">
                        {lang === 'FR' ? 'Réponse vérifiée' : 'Verified Answer'}
                      </span>
                    </div>
                    <p className="font-sans text-sm text-[#121212] leading-relaxed mb-4" data-id="element-843">
                      {lang === 'FR' ? <>
                          Le secteur C est le plus bas à{' '}
                          <span className="text-[#4F46E5] font-medium" data-id="element-844">
                            38{'\u00A0'}%
                          </span>
                          , en baisse de 6 points vs le trimestre précédent. Les
                          secteurs A et E demeurent les plus forts.
                        </> : <>
                          Area C is lowest at{' '}
                          <span className="text-[#4F46E5] font-medium" data-id="element-845">
                            38%
                          </span>
                          , down 6 points vs. last quarter. Areas A and E remain
                          strongest.
                        </>}
                    </p>
                    <div className="space-y-1.5" aria-hidden="true" data-id="element-846">
                      {AREA_PERFORMANCE.map(a => {
                      const low = a.value < 50;
                      return <div key={a.area} className="flex items-center gap-3" data-id="element-847">
                            <span className="font-sans text-[11px] text-[#121212]/50 w-12 shrink-0" data-id="element-848">
                              {lang === 'FR' ? a.areaFr : a.area}
                            </span>
                            <div className="flex-1 h-2 bg-[#F2F2F4]" data-id="element-849">
                              <div className={`h-2 ${low ? 'bg-[#4F46E5]' : 'bg-[#121212]/25'}`} style={{
                            width: `${a.value}%`
                          }} data-id="element-850" />
                            </div>
                            <span className="font-sans text-[11px] text-[#121212]/50 w-8 text-right" data-id="element-851">
                              {pct(a.value)}
                            </span>
                          </div>;
                    })}
                    </div>
                    <div className="flex items-center gap-1.5 mt-4 font-sans text-[11px] text-[#121212]/45" data-id="element-852">
                      <ShieldCheckIcon className="w-3.5 h-3.5 text-[#4F46E5]" data-id="element-853" />
                      {lang === 'FR' ? 'Réponse illustrative · Données fictives agrégées' : 'Illustrative Response · Aggregated Fictional Data'}
                    </div>
                  </div>

                  {/* Example prompts */}
                  <div className="px-5 py-4" data-id="element-854">
                    <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-3" data-id="element-855">
                      {lang === 'FR' ? "Exemples d'aperçus pour la direction" : 'Executive Insight Examples'}
                    </div>
                    <div className="space-y-2" data-id="element-856">
                      {EXAMPLE_QUESTIONS.map(q => <button key={q.en} type="button" className="w-full text-left flex items-start gap-2 border border-[#E4E6EC] px-3 py-2 font-sans text-xs text-[#121212]/70 hover:border-[#4F46E5] hover:text-[#4F46E5] transition-colors" data-id="element-857">
                          <SearchIcon className="w-3.5 h-3.5 shrink-0 mt-0.5 text-[#121212]/30" data-id="element-858" />
                          {lang === 'FR' ? q.fr : q.en}
                        </button>)}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2: CONTRAST */}
        <section className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-859">
          <div className="max-w-4xl mx-auto" data-id="element-860">
            <div className="text-center mb-20" data-id="element-861">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-6 leading-tight" data-id="element-862">
                {lang === 'FR' ? 'Fouiller des rapports prend des heures. Demander prend une phrase.' : 'Digging through reports takes hours. Asking takes a sentence.'}
              </motion.h2>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="bg-white border border-[#E4E6EC] overflow-hidden mb-12" data-id="element-863">
              <div className="grid grid-cols-2 border-b border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-864">
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase border-r border-[#E4E6EC]" data-id="element-865">
                  {lang === 'FR' ? 'Traditionnel' : 'Traditional'}
                </div>
                <div className="p-6 font-sans text-xs font-semibold tracking-widest text-[#4F46E5] uppercase" data-id="element-866">
                  {lang === 'FR' ? 'BCITI Analytique' : 'BCITI Analytics'}
                </div>
              </div>
              {COMPARISON_ROWS.map((row, i) => <div key={i} className="grid grid-cols-2 border-b border-[#E4E6EC] last:border-0 hover:bg-[#FAFAFB]/50 transition-colors" data-id="element-867">
                  <div className="p-6 font-serif text-lg text-[#121212]/50 border-r border-[#E4E6EC]" data-id="element-868">
                    {lang === 'FR' ? row.traditionalFr : row.traditional}
                  </div>
                  <div className="p-6 font-serif text-lg text-[#121212]" data-id="element-869">
                    {lang === 'FR' ? row.bcitiFr : row.bciti}
                  </div>
                </div>)}
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} className="text-center max-w-2xl mx-auto" data-id="element-870">
              <div className="w-16 h-px bg-[#E4E6EC] mx-auto mb-8" aria-hidden="true" data-id="element-871" />
              <p className="font-serif text-3xl text-[#121212]" data-id="element-872">
                {lang === 'FR' ? 'Les rapports décrivent le passé.' : 'Reports describe the past.'}
              </p>
              <p className="font-serif text-3xl text-[#121212] mt-2" data-id="element-873">
                {lang === 'FR' ? 'Les questions révèlent la prochaine action.' : 'Questions reveal the next action.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 3: VALUE — every module already has analytics */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-874">
          <div className="max-w-6xl mx-auto" data-id="element-875">
            <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-876">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-8 leading-tight" data-id="element-877">
                {lang === 'FR' ? 'Chaque module possède déjà son analytique. ATNA les fait parler ensemble.' : 'Every module already has analytics. ATNA makes them speak together.'}
              </motion.h2>
              <motion.div {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="space-y-6 font-sans text-xl text-[#121212]/70 leading-relaxed" data-id="element-878">
                {lang === 'FR' ? <>
                    <p data-id="element-879">
                      L'analytique par module est déjà en production. Le tableau
                      de bord conversationnel s'y superpose.
                    </p>
                    <p data-id="element-880">
                      Plus vous déployez de modules, plus la conversation
                      s'enrichit.
                    </p>
                  </> : <>
                    <p data-id="element-881">
                      Per-module analytics is already in production. The
                      conversational dashboard sits on top of it.
                    </p>
                    <p data-id="element-882">
                      The more modules you deploy, the richer the conversation
                      becomes.
                    </p>
                  </>}
              </motion.div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start mb-20" data-id="element-883">
              {/* SINGLE WORKFLOW */}
              <motion.div {...reveal} transition={{
              duration: 0.7,
              delay: 0.2
            }} className="lg:col-span-2 flex flex-col items-center" data-id="element-884">
                {WORKFLOW.map((node, i, arr) => {
                const isAtna = node.en === 'ATNA Unifies';
                const isLast = i === arr.length - 1;
                return <Fragment key={node.en} data-id="element-885">
                      <div className={`w-full max-w-md py-4 px-6 text-center border font-sans text-sm ${isAtna ? 'border-[#4F46E5] bg-[#4F46E5]/5 text-[#4F46E5] font-medium' : isLast ? 'border-[#121212] bg-[#121212] text-white font-medium' : 'border-[#E4E6EC] bg-white text-[#121212]'}`} data-id="element-886">
                        {lang === 'FR' ? node.fr : node.en}
                      </div>
                      {i < arr.length - 1 && <ArrowDownIcon className="w-4 h-4 text-[#E4E6EC] my-2" data-id="element-887" />}
                    </Fragment>;
              })}
              </motion.div>

              {/* Module list (subtle, not a workflow) */}
              <motion.div {...reveal} transition={{
              duration: 0.7,
              delay: 0.3
            }} className="border border-[#E4E6EC] bg-[#FAFAFB] p-8" data-id="element-888">
                <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-6" data-id="element-889">
                  {lang === 'FR' ? 'Chaque module déployé enrichit la conversation.' : 'Every module deployed adds to the conversation.'}
                </div>
                <ul className="space-y-3 mb-6" data-id="element-890">
                  {MODULE_LIST.map(mod => <li key={mod.en} className="flex items-center gap-2 font-sans text-sm text-[#121212]/70" data-id="element-891">
                      <span className={`inline-block w-1.5 h-1.5 ${mod.en === 'Analytics' ? 'bg-[#4F46E5]' : 'bg-[#121212]/20'}`} data-id="element-892" />
                      {lang === 'FR' ? mod.fr : mod.en}
                    </li>)}
                </ul>
                <div className="pt-6 border-t border-[#E4E6EC]" data-id="element-893">
                  <p className="font-serif text-xl text-[#121212]" data-id="element-894">
                    {lang === 'FR' ? 'Une seule réponse.' : 'One answer.'}
                  </p>
                </div>
              </motion.div>
            </div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.4
          }} className="text-center max-w-2xl mx-auto" data-id="element-895">
              <p className="font-serif text-2xl text-[#121212]/50" data-id="element-896">
                {lang === 'FR' ? "L'analytique ne surveille pas la plateforme." : 'Analytics does not watch the platform.'}
              </p>
              <p className="font-serif text-2xl text-[#121212] mt-1" data-id="element-897">
                {lang === 'FR' ? 'Elle aide à la piloter.' : 'It helps steer it.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 4: PRODUCTION PROOF + SEPARATION PANEL */}
        <section id="proof" className="py-32 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB] scroll-mt-24" data-id="element-898">
          <div className="max-w-5xl mx-auto" data-id="element-899">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] text-center mb-16" data-id="element-900">
              {lang === 'FR' ? 'En production. Avec de vraies données.' : 'In Production. With Real Data.'}
            </motion.h2>

            {/* Proof cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E4E6EC] border border-[#E4E6EC] mb-8" data-id="element-901">
              {(lang === 'FR' ? [{
              stat: '31,6\u00A0%',
              label: "Taux d'engagement",
              desc: 'Clarence-Rockland'
            }, {
              stat: '90 jours',
              label: 'Comparaison intégrée',
              desc: ''
            }, {
              stat: 'Depuis 2017',
              label: 'Infrastructure éprouvée',
              desc: ''
            }] : [{
              stat: '31.6%',
              label: 'Engagement Rate',
              desc: 'Clarence-Rockland'
            }, {
              stat: '90 Days',
              label: 'Built-In Comparison',
              desc: ''
            }, {
              stat: 'Since 2017',
              label: 'Proven Infrastructure',
              desc: ''
            }]).map(card => <div key={card.label} className="bg-white p-10" data-id="element-902">
                  <div className="font-serif text-3xl md:text-4xl text-[#121212] mb-3" data-id="element-903">
                    {card.stat}
                  </div>
                  <div className="font-sans text-sm font-semibold tracking-wide text-[#4F46E5] uppercase" data-id="element-904">
                    {card.label}
                  </div>
                  {card.desc && <div className="font-sans text-sm text-[#121212]/55 mt-2" data-id="element-905">
                      {card.desc}
                    </div>}
                </div>)}
            </div>

            <p className="font-sans text-lg text-[#121212]/70 text-center max-w-3xl mx-auto mb-16" data-id="element-906">
              {lang === 'FR' ? "L'indicateur d'engagement provient du déploiement actif de Clarence-Rockland." : 'The engagement metric comes from the live Clarence-Rockland deployment.'}
            </p>

            {/* Hairline divider — separates the real, measured metrics above from the illustrative dashboard preview below */}
            <div className="w-full h-px bg-[#E4E6EC] mb-16" aria-hidden="true" data-id="element-907" />

            {/* Real operational dashboard (available today) */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="bg-white border border-[#E4E6EC] mb-20" data-id="element-908">
              <div className="flex items-center justify-between px-6 py-4 border-b border-[#E4E6EC]" data-id="element-909">
                <div className="flex items-center gap-2" data-id="element-910">
                  <span className="inline-block w-3 h-3 bg-[#4F46E5]" aria-hidden="true" data-id="element-911" />
                  <span className="font-sans text-xs font-semibold tracking-[0.16em] uppercase text-[#121212]/70" data-id="element-912">
                    {lang === 'FR' ? "Tableau de bord des opérations · En direct aujourd'hui" : 'Operations Dashboard · Live Today'}
                  </span>
                </div>
                <span className="font-sans text-[11px] text-[#121212]/40" data-id="element-913">
                  {lang === 'FR' ? 'Tableau de bord illustratif' : 'Illustrative Dashboard'}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E4E6EC]" data-id="element-914">
                <div className="bg-white p-6 md:col-span-2" data-id="element-915">
                  <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-4" data-id="element-916">
                    {lang === 'FR' ? 'Engagement par secteur' : 'Engagement by Area'}
                  </div>
                  <div className="space-y-2.5" data-id="element-917">
                    {AREA_PERFORMANCE.map(a => {
                    const low = a.value < 50;
                    return <div key={a.area} className="flex items-center gap-3" data-id="element-918">
                          <span className="font-sans text-xs text-[#121212]/50 w-14 shrink-0" data-id="element-919">
                            {lang === 'FR' ? a.areaFr : a.area}
                          </span>
                          <div className="flex-1 h-2.5 bg-[#F2F2F4]" data-id="element-920">
                            <div className={`h-2.5 ${low ? 'bg-[#4F46E5]' : 'bg-[#121212]/25'}`} style={{
                          width: `${a.value}%`
                        }} data-id="element-921" />
                          </div>
                          <span className="font-sans text-xs text-[#121212]/50 w-9 text-right" data-id="element-922">
                            {pct(a.value)}
                          </span>
                        </div>;
                  })}
                  </div>
                </div>

                <div className="bg-white p-6" data-id="element-923">
                  <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-4" data-id="element-924">
                    {lang === 'FR' ? 'Tendance de croissance' : 'Growth Trend'}
                  </div>
                  <div className="flex items-end gap-1.5 h-24" aria-hidden="true" data-id="element-925">
                    {TREND.map((h, i) => <div key={i} className={`flex-1 ${i === TREND.length - 1 ? 'bg-[#4F46E5]' : 'bg-[#E4E6EC]'}`} style={{
                    height: `${h}%`
                  }} data-id="element-926" />)}
                  </div>
                </div>

                <div className="bg-white p-6" data-id="element-927">
                  <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-3" data-id="element-928">
                    {lang === 'FR' ? 'Comparaison de périodes' : 'Period Comparison'}
                  </div>
                  <div className="flex items-center gap-2 text-[#4F46E5] mb-1" data-id="element-929">
                    <span className="font-serif text-2xl" data-id="element-930">
                      {lang === 'FR' ? '+5,2 pts' : '+5.2 pts'}
                    </span>
                  </div>
                  <div className="font-sans text-xs text-[#121212]/50" data-id="element-931">
                    {lang === 'FR' ? 'vs les 90 jours précédents' : 'vs. previous 90 days'}
                  </div>
                </div>

                <div className="bg-white p-6" data-id="element-932">
                  <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-3" data-id="element-933">
                    {lang === 'FR' ? 'Activité des abonnés' : 'Subscriber Activity'}
                  </div>
                  <div className="font-serif text-2xl text-[#121212] mb-1" data-id="element-934">
                    {lang === 'FR' ? 'Active' : 'Active'}
                  </div>
                  <div className="font-sans text-xs text-[#121212]/50" data-id="element-935">
                    {lang === 'FR' ? 'Ouvertures hebdomadaires en hausse' : 'Weekly opens trending up'}
                  </div>
                </div>

                <div className="bg-white p-6" data-id="element-936">
                  <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-3" data-id="element-937">
                    {lang === 'FR' ? 'Participation à la collecte' : 'Collection Participation'}
                  </div>
                  <div className="font-serif text-2xl text-[#121212] mb-1" data-id="element-938">
                    {lang === 'FR' ? 'Élevée' : 'High'}
                  </div>
                  <div className="font-sans text-xs text-[#121212]/50" data-id="element-939">
                    {lang === 'FR' ? "Les rappels stimulent l'usage" : 'Reminders driving usage'}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* SEPARATION PANEL — Available Today vs Product Preview */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="mb-20" data-id="element-940">
              <h3 className="font-serif text-3xl text-[#121212] text-center mb-12" data-id="element-941">
                {lang === 'FR' ? "Disponible aujourd'hui vs Aperçu produit" : 'Available Today vs Product Preview'}
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E4E6EC] border border-[#E4E6EC]" data-id="element-942">
                {/* Live Today */}
                <div className="bg-white p-10" data-id="element-943">
                  <div className="inline-flex items-center gap-2 font-sans text-xs font-semibold tracking-[0.16em] uppercase text-[#121212]/70 border border-[#121212]/15 px-3 py-1.5 mb-8" data-id="element-944">
                    <CheckCircle2Icon className="w-4 h-4 text-[#121212]/70" data-id="element-945" />
                    {lang === 'FR' ? "En direct aujourd'hui" : 'Live Today'}
                  </div>
                  <ul className="space-y-5" data-id="element-946">
                    {(lang === 'FR' ? ['Analytique par module', 'Déploiement à Clarence-Rockland', 'De vrais tableaux de bord opérationnels'] : ['Per-module analytics', 'Clarence-Rockland deployment', 'Real operational dashboards']).map(item => <li key={item} className="font-serif text-xl text-[#121212] flex items-start gap-3" data-id="element-947">
                        <CheckCircle2Icon className="w-5 h-5 text-[#121212]/40 shrink-0 mt-1" data-id="element-948" />
                        {item}
                      </li>)}
                  </ul>
                </div>

                {/* Product Preview */}
                <div className="bg-[#4F46E5]/[0.03] p-10" data-id="element-949">
                  <div className="mb-8" data-id="element-950">
                    <PreviewBadge lang={lang} data-id="element-951" />
                  </div>
                  <ul className="space-y-5" data-id="element-952">
                    {(lang === 'FR' ? ['Tableau de bord conversationnel', 'Maquettes terminées', 'Feuille de route de déploiement en cours'] : ['Conversational dashboard', 'Mockups completed', 'Deployment roadmap underway']).map(item => <li key={item} className="font-serif text-xl text-[#121212] flex items-start gap-3" data-id="element-953">
                        <SparklesIcon className="w-5 h-5 text-[#4F46E5] shrink-0 mt-1" data-id="element-954" />
                        {item}
                      </li>)}
                  </ul>
                </div>
              </div>
              <p className="font-sans text-sm text-[#121212]/50 text-center mt-6" data-id="element-955">
                {lang === 'FR' ? "L'analytique conversationnelle est un aperçu produit — pas encore déployée en production." : 'Conversational analytics is a product preview — not yet deployed in production.'}
              </p>
            </motion.div>

            {/* RESEARCH BLOCK */}
            <div className="bg-white border border-[#4F46E5]/20 p-12 relative shadow-sm" data-id="element-956">
              <div className="absolute -top-4 left-12 bg-[#4F46E5] text-white font-sans text-xs font-bold tracking-widest uppercase px-4 py-2" data-id="element-957">
                {lang === 'FR' ? 'Résultat fondé sur la recherche' : 'Research-Based Outcome'}
              </div>
              <p className="font-serif text-2xl md:text-3xl text-[#121212] leading-snug mb-8 mt-2" data-id="element-958">
                {lang === 'FR' ? <>
                    Des campagnes éducatives appuyées par l'analytique ont
                    réduit la contamination du recyclage de{' '}
                    <span className="text-[#4F46E5]" data-id="element-959">24{'\u00A0'}%</span> à
                    moins de{' '}
                    <span className="text-[#4F46E5]" data-id="element-960">4{'\u00A0'}%</span> dans
                    une municipalité ontarienne.
                  </> : <>
                    Educational campaigns supported by analytics reduced
                    recycling contamination from{' '}
                    <span className="text-[#4F46E5]" data-id="element-961">24%</span> to under{' '}
                    <span className="text-[#4F46E5]" data-id="element-962">4%</span> in an Ontario
                    municipality.
                  </>}
              </p>
              <p className="font-sans text-xs text-[#121212]/45 leading-relaxed max-w-3xl" data-id="element-963">
                {lang === 'FR' ? "Résultats d'audits municipaux rapportés dans la littérature évaluée par les pairs. Les résultats varient selon le déploiement et la participation." : 'Municipal audit results referenced in peer-reviewed literature. Results vary by deployment and participation.'}
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 5: ATNA INSIGHTS — the ONLY Powered by ATNA appearance */}
        <section className="py-32 px-6 border-t border-[#E4E6EC]" data-id="element-964">
          <div className="max-w-5xl mx-auto" data-id="element-965">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-966">
              <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-8" data-id="element-967">
                ATNA Insights
              </motion.h2>
              <motion.div {...reveal} transition={{
              duration: 0.7,
              delay: 0.1
            }} className="space-y-3 font-sans text-xl text-[#121212]/70 leading-relaxed" data-id="element-968">
                <p data-id="element-969">
                  {lang === 'FR' ? 'Interrogez vos données en langage clair. ATNA répond à partir de vos vraies données organisationnelles.' : 'Query your data in plain language. ATNA answers using your real organizational data.'}
                </p>
                <p className="font-sans text-base text-[#121212]/55" data-id="element-970">
                  {lang === 'FR' ? 'Vérifié. Traçable. Auditable. Aucune supposition.' : 'Verified. Traceable. Auditable. No guessing.'}
                </p>
              </motion.div>
            </div>

            {/* ATNA Insights panel */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.15
          }} className="border border-[#4F46E5]/20 bg-[#4F46E5]/[0.02] p-8 md:p-12 relative mb-12" data-id="element-971">
              <div className="absolute -top-3 left-8 md:left-12 bg-[#4F46E5] text-white font-sans text-[10px] font-bold tracking-widest uppercase px-3 py-1.5" data-id="element-972">
                {lang === 'FR' ? 'Propulsé par ATNA' : 'Powered by ATNA'}
              </div>
              <div className="flex items-center justify-between mb-8 mt-2 flex-wrap gap-3" data-id="element-973">
                <div className="flex items-center gap-3" data-id="element-974">
                  <SparklesIcon className="w-5 h-5 text-[#4F46E5]" data-id="element-975" />
                  <span className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase" data-id="element-976">
                    {lang === 'FR' ? 'Intelligence conversationnelle' : 'Conversational Intelligence'}
                  </span>
                </div>
                <PreviewBadge lang={lang} data-id="element-977" />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-978">
                {/* Example prompts */}
                <div data-id="element-979">
                  <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-4" data-id="element-980">
                    {lang === 'FR' ? 'Demander en langage clair' : 'Ask in plain language'}
                  </div>
                  <div className="space-y-2.5" data-id="element-981">
                    {EXAMPLE_QUESTIONS.map(q => <div key={q.en} className="flex items-start gap-3 border border-[#E4E6EC] bg-white px-4 py-3 font-sans text-sm text-[#121212]/80" data-id="element-982">
                        <SearchIcon className="w-4 h-4 text-[#4F46E5]/60 shrink-0 mt-0.5" data-id="element-983" />
                        {lang === 'FR' ? q.fr : q.en}
                      </div>)}
                  </div>
                </div>

                {/* Illustrative response */}
                <div data-id="element-984">
                  <div className="font-sans text-[10px] font-semibold tracking-[0.16em] uppercase text-[#121212]/40 mb-4" data-id="element-985">
                    {lang === 'FR' ? 'Exemple de réponse' : 'Example answer'}
                  </div>
                  <div className="border border-[#4F46E5]/20 bg-white p-5" data-id="element-986">
                    <div className="font-sans text-sm text-[#121212]/50 mb-3" data-id="element-987">
                      {lang === 'FR' ? '« Quelle est la répartition des requêtes par sujet ? »' : "“What's the share of requests by topic?”"}
                    </div>
                    <div className="space-y-2.5 mb-4" aria-hidden="true" data-id="element-988">
                      {(lang === 'FR' ? [{
                      topic: 'Routes et trottoirs',
                      value: 34
                    }, {
                      topic: 'Déchets et recyclage',
                      value: 27
                    }, {
                      topic: 'Permis',
                      value: 21
                    }, {
                      topic: 'Autre',
                      value: 18
                    }] : [{
                      topic: 'Roads & Sidewalks',
                      value: 34
                    }, {
                      topic: 'Waste & Recycling',
                      value: 27
                    }, {
                      topic: 'Permits',
                      value: 21
                    }, {
                      topic: 'Other',
                      value: 18
                    }]).map(r => <div key={r.topic} className="flex items-center gap-3" data-id="element-989">
                          <span className="font-sans text-xs text-[#121212]/60 w-32 shrink-0" data-id="element-990">
                            {r.topic}
                          </span>
                          <div className="flex-1 h-2.5 bg-[#F2F2F4]" data-id="element-991">
                            <div className="h-2.5 bg-[#4F46E5]" style={{
                          width: `${r.value}%`
                        }} data-id="element-992" />
                          </div>
                          <span className="font-sans text-xs text-[#121212]/50 w-8 text-right" data-id="element-993">
                            {pct(r.value)}
                          </span>
                        </div>)}
                    </div>
                    <div className="flex items-center gap-1.5 pt-3 border-t border-[#E4E6EC] font-sans text-[11px] text-[#121212]/45" data-id="element-994">
                      <ShieldCheckIcon className="w-3.5 h-3.5 text-[#4F46E5]" data-id="element-995" />
                      {lang === 'FR' ? 'Réponse illustrative · Données fictives agrégées' : 'Illustrative Response · Aggregated Fictional Data'}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Supporting capabilities */}
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} data-id="element-996">
              <div className="font-sans text-xs font-semibold tracking-widest text-[#121212]/40 uppercase mb-6 text-center" data-id="element-997">
                {lang === 'FR' ? 'Ces éléments soutiennent la couche conversationnelle' : 'These support the conversational layer'}
              </div>
              <div className="flex flex-wrap justify-center gap-4 mb-6" data-id="element-998">
                {SUPPORTING_CAPS.map(cap => <div key={cap.en} className="px-5 py-2.5 border border-[#E4E6EC] bg-white font-sans text-sm text-[#121212]/70" data-id="element-999">
                    {lang === 'FR' ? cap.fr : cap.en}
                  </div>)}
              </div>
              <p className="font-sans text-sm text-[#121212]/50 text-center" data-id="element-1000">
                {lang === 'FR' ? "L'analytique par module est disponible en production aujourd'hui." : 'Per-module analytics is available in production today.'}
              </p>
            </motion.div>
          </div>
        </section>

        {/* SECTION 6: FINAL POSITIONING */}
        <section className="py-40 px-6 border-t border-[#E4E6EC] bg-[#FAFAFB]" data-id="element-1001">
          <div className="max-w-3xl mx-auto text-center" data-id="element-1002">
            <motion.h2 {...reveal} className="font-serif text-4xl md:text-5xl text-[#121212] mb-10 leading-tight" data-id="element-1003">
              {lang === 'FR' ? 'Vos données ont déjà les réponses. Maintenant elles parlent.' : 'Your data already has the answers. Now it speaks.'}
            </motion.h2>
            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.1
          }} className="space-y-2 font-sans text-xl text-[#121212]/70 mb-12" data-id="element-1004">
              <p data-id="element-1005">
                {lang === 'FR' ? "Les organisations capables d'interroger leurs données en une phrase ne reviennent pas aux rapports trimestriels." : 'Organizations that can query their data in a sentence do not return to quarterly reporting.'}
              </p>
              <div className="flex items-center justify-center gap-3 pt-6" data-id="element-1006">
                <PreviewBadge lang={lang} data-id="element-1007" />
                <span className="font-sans text-sm text-[#121212]/50" data-id="element-1008">
                  {lang === 'FR' ? 'Disponible en démo.' : 'Available in demo.'}
                </span>
              </div>
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="mb-16" data-id="element-1009">
              <div className="w-16 h-px bg-[#E4E6EC] mx-auto mb-8" data-id="element-1010" />
              <p className="font-serif text-2xl text-[#121212]/50" data-id="element-1011">
                {lang === 'FR' ? 'La plateforme génère les données.' : 'The platform generates data.'}
              </p>
              <p className="font-serif text-2xl text-[#121212]/50" data-id="element-1012">
                {lang === 'FR' ? "L'analytique révèle le sens." : 'Analytics reveals meaning.'}
              </p>
              <p className="font-serif text-2xl text-[#121212]" data-id="element-1013">
                {lang === 'FR' ? 'ATNA transforme le sens en action.' : 'ATNA turns meaning into action.'}
              </p>
            </motion.div>

            <motion.div {...reveal} transition={{
            duration: 0.7,
            delay: 0.3
          }} data-id="element-1014">
              <Link to="/request-demo" onClick={() => track('Demo CTA Clicked', {
              location: 'Analytics Final'
            })} className="inline-flex items-center gap-2 px-8 py-4 bg-[#4F46E5] text-white text-base font-medium rounded-none hover:bg-[#4F46E5]/90 transition-colors" data-id="element-1015">
                {lang === 'FR' ? 'Demander une démo' : 'Request a Demo'}
                <ArrowUpRightIcon className="w-4 h-4" strokeWidth={2} data-id="element-1016" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer data-id="element-1017" />
    </div>;
}